import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./FocusModeToggle.module.css";

export interface FocusModeToggleProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  keyboardShortcut?: string;
}

export function FocusModeToggle({
  enabled,
  onToggle,
  keyboardShortcut = "Mod+Shift+F",
}: FocusModeToggleProps) {
  const { t } = useTranslation();
  const handleToggle = useCallback(() => {
    onToggle(!enabled);
  }, [enabled, onToggle]);

  // Handle keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.shiftKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        handleToggle();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleToggle]);

  return (
    <button
      type="button"
      className={`${styles.toggle} ${enabled ? styles.toggleActive : ""}`}
      onClick={handleToggle}
      title={`${enabled ? t("editor.focusModeOn") : t("editor.focusModeOff")} (${keyboardShortcut.replace("Mod", "⌘/Ctrl")})`}
      aria-pressed={enabled}
    >
      <FocusIcon />
      <span className={styles.label}>{t("editor.focusMode")}</span>
      {enabled && <span className={styles.indicator} />}
    </button>
  );
}

function FocusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M8 1V3M8 13V15M1 8H3M13 8H15M3.05 3.05L4.46 4.46M11.54 11.54L12.95 12.95M3.05 12.95L4.46 11.54M11.54 4.46L12.95 3.05"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
